import React, { ReactElement, Suspense, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Column, FlexBox, GridContainer, H2, Row } from '@vp/swan';
import { ValidationState } from '@vp/upload-and-print-validator';
import { useDebugContext } from 'src/hooks/DebugContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useUploadContext } from 'src/hooks/UploadContextHooks';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { usePromptContext } from 'src/providers/PromptContextProvider';
import { text } from 'src/utils/localizationUtil';
import { filterRecommendedProductOptionsToJustProductPageOptions } from 'src/utils/productOptionUtils';
import { FileRequirements } from './FileRequirements';
import { FooterButton } from './FooterButton';
import { PageSizeWarningCallout } from './PageSizeWarningCallout';
import { PreUploadedFileProcessor } from './PreUploadedFileProcessor';
import { PreviewComponent } from './PreviewComponent';
import PricingInformation from './PricingInformation';
import { SuggestedProductOption } from './SuggestedProductOption';
import { SherbertUploader } from './Uploader/SherbertUploader';
import { ValidationCard } from './ValidationCard';
import { ValidationErrorCardContainer } from './ValidationErrorCardContainer';

const ProductOptionChangesPrompt = React.lazy(() =>
  import('./ProductOptionChangesPrompt').then(mod => ({ default: mod.ProductOptionChangesPrompt }))
);

const ValidationErrorPrompt = React.lazy(() =>
  import('./ValidationErrorPrompt').then(mod => ({ default: mod.ValidationErrorPrompt }))
);

import './uploadAndPrintForm.css';

export type UploadAndPrintFormProps = {
  isMobile: boolean;
};

const UploadAndPrintForm = ({ isMobile }: UploadAndPrintFormProps): ReactElement => {
  const { upload, setUpload, isPreUploaded, isUploading } = useUploadContext();
  const { productInformation, setProductInformation, completedProductInformation } = useProductContext();
  const { validations, validationPlaceholders, setValidations, isValidating } = useValidationContext();
  const {
    showValidationErrorPrompt,
    showProductOptionsPrompt,
    setShowProductOptionsPrompt,
    setShowValidationErrorPrompt,
  } = usePromptContext();
  const { debugMode } = useDebugContext();

  //These are split out from Upload in order to have more granular dependencies in the below useEffect
  //If that useEffect has a direct dependency on Upload, it will re-trigger whenever Upload changes based on
  //a reference equality check, and because sendToPrepress updates the upload object, that will trigger an infinite
  //loop where this useEffect retriggers itself again and again.
  const uploadFileUri = upload?.fileUri ?? '';
  const uploadFileName = upload?.uploadedFileName ?? '';
  const uploadThumbnailUri = upload?.thumbnailUrl ?? '';

  useEffect(() => {
    const applyProductOptionRecommendations = async () => {
      if (productInformation.recommendedProductData !== undefined && validations) {
        let recommendationsAreDifferentFromCurrentSelection = false;

        const productOptionsToAccept = await filterRecommendedProductOptionsToJustProductPageOptions(
          productInformation.productKey,
          productInformation.productVersion,
          productInformation.recommendedProductData.productOptions
        );

        Object.entries(productOptionsToAccept).forEach(([key, value]) => {
          if (
            (productInformation.selectedOptions[key] && productInformation.selectedOptions[key] !== value) ||
            (completedProductInformation?.selectedOptions[key] &&
              completedProductInformation.selectedOptions[key] !== value)
          ) {
            recommendationsAreDifferentFromCurrentSelection = true;
          }
        });

        setShowProductOptionsPrompt(recommendationsAreDifferentFromCurrentSelection);
        if (recommendationsAreDifferentFromCurrentSelection) {
          setShowValidationErrorPrompt(false);
        }
      } else {
        setShowProductOptionsPrompt(false);
      }
    };

    applyProductOptionRecommendations();
  }, [
    productInformation.recommendedProductData,
    productInformation.selectedOptions,
    productInformation.mpvId,
    productInformation.productKey,
    productInformation.productName,
    productInformation.productVersion,
    productInformation.quantity,
    uploadFileUri,
    uploadFileName,
    uploadThumbnailUri,
    completedProductInformation,
    setProductInformation,
    setUpload,
    setValidations,
    validations,
    setShowProductOptionsPrompt,
    setShowValidationErrorPrompt,
  ]);

  const showErrorCards = useMemo(() => {
    if (isValidating || isUploading) {
      return false;
    }

    let showErrors = false;

    /**
     * Check if there are any errors in the validation.
     */
    if (validations || validationPlaceholders) {
      showErrors = (validations ?? validationPlaceholders ?? []).some(
        validationState => validationState.state === ValidationState.Error
      );
    }

    return showErrors;
  }, [isUploading, isValidating, validationPlaceholders, validations]);

  const showPrompts = useMemo(
    () => !(isValidating || isUploading) && (showValidationErrorPrompt || showProductOptionsPrompt),
    [isValidating, isUploading, showValidationErrorPrompt, showProductOptionsPrompt]
  );

  return (
    <GridContainer>
      <Row sticky={!isMobile}>
        {(!isMobile || (isPreUploaded && upload?.renderingInstructionsUrl && completedProductInformation)) && (
          <Column
            span={8}
            backgroundColor="strong"
            sticky
            className={classNames('content-screen', debugMode && 'debug-mode-height')}
          >
            <PreviewComponent />
          </Column>
        )}
        <Column
          span={4}
          className={classNames('content-screen', 'design-review-form', debugMode && 'debug-mode-height')}
          padding={0}
        >
          <FlexBox
            flexDirection="column"
            className="review-wrapper"
            paddingX={isMobile ? 5 : 8}
            paddingTop={isMobile ? 4 : 9}
          >
            <Suspense fallback={null}>
              <ProductOptionChangesPrompt isMobile={false} />
            </Suspense>
            <Suspense fallback={null}>
              {!productInformation.recommendedProductData && showPrompts && <ValidationErrorPrompt />}
            </Suspense>
            <FlexBox flexDirection="column" gap={4}>
              <H2 fontSkin="title-headline">{text('form-header')}</H2>
              <PricingInformation />
            </FlexBox>
            {isPreUploaded ? (
              <PreUploadedFileProcessor isMobile={isMobile} />
            ) : (
              <SherbertUploader isMobile={isMobile} />
            )}
            {isMobile && <PageSizeWarningCallout />}
            {productInformation.recommendedProductData && !showPrompts ? (
              <>
                <ValidationCard
                  cardTitle={text('product-adjustment-required')}
                  cardMessage={text('product-adjustment-error-message')}
                  type="error"
                />
                <SuggestedProductOption isMobile={isMobile} />
              </>
            ) : showErrorCards && !showPrompts ? (
              <ValidationErrorCardContainer
                showCardTitle={true}
                bodyDescription={text('file-requirement.bodyDescription')}
              />
            ) : (
              <FileRequirements isMobile={isMobile} />
            )}
            {!isMobile && <PageSizeWarningCallout />}
          </FlexBox>
          <FooterButton showErrorCards={showErrorCards} isMobile={isMobile} />
        </Column>
      </Row>
    </GridContainer>
  );
};

export { UploadAndPrintForm };
